/**
 * Cloud save bridge (#450) — the PARENT side of the FS-hook persistence.
 *
 * Pairs with the FS-hook inline script injected by `PyxelPlayer.vue` (active when
 * `cloudSaveSessionId` is set). The iframe script detects writes under `savedata/`
 * and posts them here; we debounce and PUT to the server (authenticated) or fall
 * back to localStorage (unauthenticated). On start we prefetch the user's savedata
 * and push it into the iframe so the game reads it back via plain `open()`.
 *
 * Security: the iframe (where user code runs) never sees the JWT or makes network
 * calls — this trusted parent context mediates all persistence. Save scope is
 * resolved SERVER-side from the session, so the authenticated path needs only the
 * session id; the `scope` arg is used only to namespace localStorage for
 * unauthenticated players. See doc/architecture/cloud_save.md.
 *
 * Protocol (postMessage, both directions, `type: '__camFs'`):
 *   iframe → parent  { op: 'ready' }                       // request a seed
 *   iframe → parent  { op: 'save', path, bytes:Uint8Array} // a tracked file closed
 *   parent → iframe  { op: 'load', files:[{path,bytes}] }  // prefetched savedata
 *
 * The hook reports EVERY tracked prefix, so this bridge handles only `.pyxel/`;
 * `shared/` (#684, in-game ranking) belongs to `useSharedData`.
 */

import { useSavedataApi } from './api/useSavedataApi'

interface PrefetchFile {
  path: string
  bytes: Uint8Array
}

interface CamFsMessage {
  type?: string
  op?: string
  path?: string
  bytes?: Uint8Array
}

// Pyxel の user_data_dir() が返す保存ルート（BASE_DIR=".pyxel"）。ネイティブ互換のため
// C&M はこの標準作法に一本化。API に送る filename はこの prefix を剥がした相対パス
// （"vendor/app/save.json"）で、seed/inject 時に再付与する。
const SAVE_PREFIX = '.pyxel/'
const DEBOUNCE_MS = 1000

function u8ToB64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

function b64ToU8(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

export function useCloudSave() {
  const { listSavedata, putSavedata } = useSavedataApi()

  let iframeEl: HTMLIFrameElement | null = null
  // #748: cross-origin フレーム経路では、送信 targetOrigin と受信 origin をこの値に絞る
  // （savedata は実ユーザーデータなので、フレームが別 origin へ navigate したら渡さない/受けない）。
  // blob 経路（opaque origin / same-origin）では undefined = 従来どおり '*' + source 照合のみ。
  let frameOrigin: string | undefined
  let sessionId = ''
  let scope: string | null = null
  let handler: ((e: MessageEvent) => void) | null = null
  let prefetchFiles: PrefetchFile[] = []
  let prefetchDone = false

  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const latest = new Map<string, Uint8Array>()

  function isAuthed(): boolean {
    return useAuthInfo().isAuthenticated
  }

  function lsPrefix(): string {
    return `cam_save:${scope ?? ''}:`
  }

  // --- localStorage fallback (unauthenticated; needs a stable scope) ---

  function readLocalAll(): PrefetchFile[] {
    if (!scope) return []
    const out: PrefetchFile[] = []
    const prefix = lsPrefix()
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key || !key.startsWith(prefix)) continue
        const filename = key.slice(prefix.length)
        const value = localStorage.getItem(key)
        if (value == null) continue
        out.push({ path: `${SAVE_PREFIX}${filename}`, bytes: b64ToU8(value) })
      }
    }
    catch (e) {
      console.error('[cloudSave] localStorage read failed', e)
    }
    return out
  }

  function writeLocal(filename: string, b64: string): void {
    if (!scope) return
    try {
      localStorage.setItem(lsPrefix() + filename, b64)
    }
    catch (e) {
      console.error('[cloudSave] localStorage write failed', e)
    }
  }

  // --- prefetch + seed ---

  async function prefetch(): Promise<void> {
    if (isAuthed() && sessionId) {
      try {
        const res = await listSavedata(sessionId)
        prefetchFiles = (res.files ?? []).map(f => ({
          path: `${SAVE_PREFIX}${f.filename}`,
          bytes: b64ToU8(f.content),
        }))
      }
      catch (e) {
        console.error('[cloudSave] prefetch failed', e)
        prefetchFiles = []
      }
    }
    else {
      prefetchFiles = readLocalAll()
    }
    prefetchDone = true
    sendSeed()
  }

  function sendSeed(): void {
    const win = iframeEl?.contentWindow
    if (!win || !prefetchDone) return
    win.postMessage({ type: '__camFs', op: 'load', files: prefetchFiles }, frameOrigin ?? '*')
  }

  // --- incoming saves ---

  function onMessage(e: MessageEvent): void {
    if (!iframeEl || e.source !== iframeEl.contentWindow) return
    if (frameOrigin && e.origin !== frameOrigin) return // #748 cross-origin 経路の受信 origin 厳格化
    const d = e.data as CamFsMessage
    if (!d || d.type !== '__camFs') return
    if (d.op === 'ready') {
      sendSeed()
      return
    }
    if (d.op === 'save' && typeof d.path === 'string' && d.bytes instanceof Uint8Array) {
      // #684: フックは `.pyxel/`（セーブ）と `shared/`（共有データ）の両方を報告する。
      // 自分の prefix 以外は触らない —— でないと共有スコアがセーブとして保存される。
      if (!d.path.startsWith(SAVE_PREFIX)) return
      scheduleSave(d.path, d.bytes)
    }
  }

  function scheduleSave(path: string, bytes: Uint8Array): void {
    const filename = path.startsWith(SAVE_PREFIX) ? path.slice(SAVE_PREFIX.length) : path
    latest.set(filename, bytes)
    const prev = timers.get(filename)
    if (prev) clearTimeout(prev)
    timers.set(filename, setTimeout(() => {
      timers.delete(filename)
      void flush(filename)
    }, DEBOUNCE_MS))
  }

  async function flush(filename: string): Promise<void> {
    const bytes = latest.get(filename)
    if (!bytes) return
    const b64 = u8ToB64(bytes)
    if (isAuthed() && sessionId) {
      try {
        await putSavedata(sessionId, filename, b64)
      }
      catch (e) {
        console.error('[cloudSave] put failed', filename, e)
      }
    }
    else {
      writeLocal(filename, b64)
    }
  }

  function flushAll(): void {
    for (const filename of Array.from(timers.keys())) {
      const t = timers.get(filename)
      if (t) clearTimeout(t)
      timers.delete(filename)
      void flush(filename)
    }
  }

  // --- lifecycle ---

  function start(
    iframe: HTMLIFrameElement,
    opts: { sessionId: string, scope: string | null, frameOrigin?: string },
  ): void {
    stop()
    iframeEl = iframe
    sessionId = opts.sessionId
    scope = opts.scope
    frameOrigin = opts.frameOrigin
    prefetchDone = false
    prefetchFiles = []
    handler = onMessage
    window.addEventListener('message', handler)
    void prefetch()
  }

  function stop(): void {
    flushAll()
    if (handler) {
      window.removeEventListener('message', handler)
      handler = null
    }
    iframeEl = null
    frameOrigin = undefined
    sessionId = ''
    scope = null
    prefetchDone = false
    prefetchFiles = []
  }

  onUnmounted(stop)

  return { start, stop }
}
